import { useLayoutEffect } from 'react';
import { useAuth } from '../auth/AuthProvider.js';

/**
 * Huisstijl per tenant (RHOGO als eigen tenant, besluit 26 okt 2026). De
 * kleuren en letters zelf staan als CSS-variabelen in src/index.css; hier
 * staat alleen wat niet in CSS kan: logo, naam en welke webfonts erbij horen.
 */
export interface TenantBrand {
  /** Heeft deze tenant een eigen huisstijl (en dus een data-tenant op <html>)? */
  eigen: boolean;
  appNaam: string;
  logo: string;
  logoAlt: string;
  /** Hoogte van het logo in de zijbalk; het RHOGO-logo is bijna vierkant. */
  logoClass: string;
  /** Alleen geladen voor deze tenant, zodat SUPPOHRT er niets extra voor binnenhaalt. */
  fontsHref?: string;
  /**
   * Teksten die in SUPPOHRT de organisatienaam noemen. RHOGO-gebruikers werken
   * niet bij SUPPOHRT, dus daar zou die naam verkeerd lezen.
   */
  kennisbank: string;
  collegas: string;
  account: string;
  beheerder: string;
}

const SUPPOHRT_BRAND: TenantBrand = {
  eigen: false,
  appNaam: 'SUPPOHRT Kennishub',
  logo: '/SUPPOHRT-logo-800px.webp',
  logoAlt: 'SUPPOHRT',
  logoClass: 'h-7 w-auto',
  kennisbank: 'SUPPOHRT-kennisbank',
  collegas: 'alle SUPPOHRT-medewerkers',
  account: 'je SUPPOHRT-account',
  beheerder: 'Een SUPPOHRT admin',
};

const EIGEN_HUISSTIJL: Record<string, TenantBrand> = {
  rhogo: {
    eigen: true,
    appNaam: 'RHOGO Kennishub',
    logo: '/RHOGO-logo-800px.webp',
    logoAlt: 'RHOGO',
    logoClass: 'h-20 w-auto',
    fontsHref:
      'https://fonts.googleapis.com/css2?family=Righteous&family=Ubuntu:ital,wght@0,400;0,500;0,700;1,400&display=swap',
    kennisbank: 'kennisbank',
    collegas: 'je RHOGO-collega’s',
    account: 'je werkaccount',
    beheerder: 'Een beheerder',
  },
};

export function brandVoor(tenantId: string | undefined): TenantBrand {
  return (tenantId && EIGEN_HUISSTIJL[tenantId]) || SUPPOHRT_BRAND;
}

export function useTenantBrand(): TenantBrand {
  const { user } = useAuth();
  return brandVoor(user?.tenantId);
}

/**
 * Zet `data-tenant` op <html> en laadt de webfonts van de tenant. Layout-effect
 * zodat de eerste geschilderde frame na /api/me al de goede kleuren heeft.
 */
export function TenantBrandSync(): null {
  const { user } = useAuth();
  const tenantId = user?.tenantId;
  useLayoutEffect(() => {
    const brand = brandVoor(tenantId);
    const html = document.documentElement;
    if (!brand.eigen || !tenantId) {
      delete html.dataset.tenant;
      return;
    }
    html.dataset.tenant = tenantId;
    if (brand.fontsHref && !document.querySelector(`link[data-tenant-fonts="${tenantId}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = brand.fontsHref;
      link.dataset.tenantFonts = tenantId;
      document.head.appendChild(link);
    }
  }, [tenantId]);
  return null;
}
